import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { prospectContactSchema } from '../../lib/validation';
import type { ProspectContactData } from '../../lib/validation';

interface Contact {
  id: string;
  full_name: string;
  title?: string;
  email?: string;
  phone?: string;
  preferred_contact_method?: string;
  linkedin_url?: string;
  is_primary: boolean;
  is_official_contact?: boolean;
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  notes?: string;
}

interface ProspectContactSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prospectId: string;
  contact?: Contact | null;
}

const CONTACT_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'in_person', label: 'In Person' },
] as const;

export default function ProspectContactSheet({ isOpen, onClose, onSuccess, prospectId, contact }: ProspectContactSheetProps) {
  const isEditing = !!contact;
  const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm<ProspectContactData>({
    resolver: zodResolver(prospectContactSchema),
    defaultValues: { is_primary: false, is_official_contact: false, country: 'Nigeria' },
  });

  const preferredMethod = watch('preferred_contact_method');
  const isPrimary = watch('is_primary');
  const isOfficial = watch('is_official_contact');

  useEffect(() => {
    if (contact) {
      reset({
        full_name: contact.full_name,
        title: contact.title || '',
        email: contact.email || '',
        phone: contact.phone || '',
        preferred_contact_method: contact.preferred_contact_method as ProspectContactData['preferred_contact_method'],
        linkedin_url: contact.linkedin_url || '',
        is_primary: contact.is_primary,
        is_official_contact: contact.is_official_contact ?? false,
        street: contact.street || '',
        city: contact.city || '',
        state: contact.state || '',
        postal_code: contact.postal_code || '',
        country: contact.country || 'Nigeria',
        notes: contact.notes || '',
      });
    } else {
      reset({ is_primary: false, is_official_contact: false, country: 'Nigeria' });
    }
  }, [contact, reset, isOpen]);

  const onSubmit = async (data: ProspectContactData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const payload = {
        full_name: data.full_name,
        title: data.title || null,
        email: data.email || null,
        phone: data.phone || null,
        preferred_contact_method: data.preferred_contact_method || null,
        linkedin_url: data.linkedin_url || null,
        is_primary: data.is_primary,
        is_official_contact: data.is_official_contact,
        street: data.street || null,
        city: data.city || null,
        state: data.state || null,
        postal_code: data.postal_code || null,
        country: data.country || null,
        notes: data.notes || null,
      };

      if (isEditing && contact) {
        const { error } = await supabase.from('prospect_contacts').update(payload).eq('id', contact.id);
        if (error) throw error;
        if (data.is_primary && !contact.is_primary) {
          await supabase.from('prospect_contacts').update({ is_primary: false }).eq('prospect_id', prospectId).neq('id', contact.id);
        }
        toast.success('Contact updated');
      } else {
        if (data.is_primary) {
          await supabase.from('prospect_contacts').update({ is_primary: false }).eq('prospect_id', prospectId);
        }
        const { data: newContact, error } = await supabase.from('prospect_contacts').insert([{ prospect_id: prospectId, ...payload }]).select().single();
        if (error) throw error;
        await supabase.from('prospect_activities').insert([{
          prospect_id: prospectId,
          activity_type: 'contact_added',
          title: `Contact added: ${data.full_name}`,
          metadata: { contact_id: newContact.id, title: data.title },
          performed_by: user?.id,
        }]);
        toast.success('Contact added');
      }

      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save contact');
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
              <Dialog.Panel className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
                  <Dialog.Title className="text-base font-semibold text-golf-gray">
                    {isEditing ? 'Edit Contact' : 'Add Contact'}
                  </Dialog.Title>
                  <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                    <XMarkIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <input {...register('full_name')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="Jane Doe" />
                      {errors.full_name && <p className="mt-1 text-xs text-red-600">{errors.full_name.message}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                      <input {...register('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="CEO" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input type="email" {...register('email')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="jane@example.com" />
                      {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input type="tel" {...register('phone')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="+234..." />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                    <input {...register('linkedin_url')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="https://linkedin.com/in/..." />
                    {errors.linkedin_url && <p className="mt-1 text-xs text-red-600">{errors.linkedin_url.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Method</label>
                    <div className="flex flex-wrap gap-2">
                      {CONTACT_METHODS.map(({ value, label }) => (
                        <button key={value} type="button" onClick={() => setValue('preferred_contact_method', value)}
                          className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${preferredMethod === value ? 'border-golf-orange bg-golf-orange/10 text-golf-orange' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPinIcon className="w-4 h-4 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-700">Mailing Address</h3>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                        <input {...register('street')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="123 Business Ave, Floor 4" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                          <input {...register('city')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="Lagos" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                          <input {...register('state')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="Lagos State" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                          <input {...register('postal_code')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="100001" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                          <input {...register('country')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="Nigeria" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea {...register('notes')} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange resize-none" />
                  </div>

                  <div className="flex flex-col gap-2 pt-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <div onClick={() => setValue('is_primary', !isPrimary)} className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${isPrimary ? 'bg-golf-orange' : 'bg-gray-300'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isPrimary ? 'translate-x-4' : ''}`} />
                      </div>
                      <div>
                        <span className="text-sm text-gray-700 font-medium">Primary contact</span>
                        <p className="text-xs text-gray-400">Main point of contact for this prospect</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <div onClick={() => setValue('is_official_contact', !isOfficial)} className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${isOfficial ? 'bg-golf-gray' : 'bg-gray-300'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isOfficial ? 'translate-x-4' : ''}`} />
                      </div>
                      <div>
                        <span className="text-sm text-gray-700 font-medium">Official contact</span>
                        <p className="text-xs text-gray-400">Formal/legal representative of the organisation</p>
                      </div>
                    </label>
                  </div>

                  <div className="flex justify-end gap-3 pt-1">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                    <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 transition-colors disabled:opacity-60">
                      {isSubmitting ? 'Saving...' : isEditing ? 'Save Contact' : 'Add Contact'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
